import * as React from 'react';
import './StockPage.scss';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useEntitiesResult } from '../../store/entities/hooks';
import { StockEntity } from '../../store/entities/state';
import { isLoadingEntities } from '../../store/entities/selectors';
import { createEntity, entityCreating, entityEditing, loadEntities, updateEntity } from '../../store/entities/actions';
import { buyStockAsync, updateStockQuantityAsync, getStockAsync, getStockHistoryAsync } from '../../ajax/requests';
import Typography from '../../components/Typography';
import Add from '@material-ui/icons/Add';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import EditIcon from '@material-ui/icons/Edit';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import RemoveIcon from '@material-ui/icons/PlaylistPlay';
import ListIcon from '@material-ui/icons/History';
import IconButton from '@material-ui/core/IconButton';
import { useEffect, useState, useMemo } from 'react';
import { UpdateStockQuantity, ExpenseData, StockData, StockHistoryData, StockHistoryType } from '../../models';
import moment from 'moment';
import ExpenseForm from '../../components/Forms/ExpenseForm';
import { NumberInputField, TextInputField } from '../../components/Forms/fields';
import { Guid } from '../../shared/utils';
import AppButton from '../../components/AppButton';

const yellowStockLimit = 5
const redStockLimit = 2

const StockPage: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const stock = useEntitiesResult<StockEntity>('stock');
    const isLoading = useSelector(isLoadingEntities('stock'));
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [buyStock, setBuyStock] = useState<{item: StockData, expense: ExpenseData}|undefined>();
    const [decreaseStock, setDecreaseStock] = useState<{item: StockData, data: UpdateStockQuantity}|undefined>();
    const [historyModal, setHistoryModal] = useState<{item: StockData, history: StockHistoryData[]}|undefined>();
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        dispatch(loadEntities('stock', () => getStockAsync(), 'NewResult'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Group stock items by category
    const stockByCategory = useMemo(() => {
        const grouped = new Map<string, StockEntity[]>();
        stock.forEach(item => {
            const category = item.category || 'Uncategorized';
            if (!grouped.has(category)) {
                grouped.set(category, []);
            }
            grouped.get(category)!.push(item);
        });
        return grouped;
    }, [stock]);

    const handleOpenCreateModal = () => {
        dispatch(entityCreating('stock', {
            id: Guid.newGuid(),
            name: '',
            category: '',
            defaultPrice: 0,
            amount: 0,
        }));
    };

    const handleOpenEditModal = (stockItem: StockEntity) => (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(entityEditing('stock', stockItem.id));
    };

    const handleOpenBuyModal = (stockItem: StockEntity) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setBuyStock({
            item: stockItem,
            expense: {
                id: Guid.newGuid(),
                name: `${t(TK.stock)} - ${stockItem.name}`,
                type: t(TK.stock),
                entity: '',
                notes: '',
                attachments: [],
                payment: {
                    date: moment.utc().format('YYYY-MM-DD'),
                    value: stockItem.defaultPrice,
                    type: '',
                },
            },
        });
    };

    const handleCloseBuyModal = () => {
        setBuyStock(undefined);
    };

    const handleCreateExpense = () => {
        if (!buyStock || !buyStock.item.id) return;

        dispatch(createEntity('expenses', buyStock.expense, () => buyStockAsync(buyStock.item.id, buyStock.expense)))
        dispatch(updateEntity('stock', {...buyStock.item, amount: buyStock.item.amount + (buyStock.expense.payment.amount ?? 1)}, async () => {}))

        handleCloseBuyModal();
    };

    const handleOpenDecreaseModal = (stockItem: StockEntity) => (e: React.MouseEvent) => {
        e.stopPropagation();
        setDecreaseStock({
            item: stockItem,
            data: {
                quantity: -1,
                reason: '',
            },
        });
    };

    const handleCloseDecreaseModal = () => {
        setDecreaseStock(undefined);
    };

    const handleDecreaseStock = async () => {
        if (!decreaseStock || !decreaseStock.item.id) return;

        setSubmitting(true);

        try {
            await updateStockQuantityAsync(decreaseStock.item.id, decreaseStock.data);
            dispatch(updateEntity('stock', {...decreaseStock.item, amount: decreaseStock.item.amount - decreaseStock.data.quantity}, async () => {}))
            handleCloseDecreaseModal();
        } catch (error) {
            console.error('Failed to decrease stock:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenHistory = (item: StockData) => async () => {
        setLoadingHistory(true);
        try {
            const history = await getStockHistoryAsync(item.id);
            setHistoryModal({item, history});
        } catch (error) {
            console.error('Failed to load stock history:', error);
        } finally {
            setLoadingHistory(false);
        }
    }

    const handleCloseHistory = () => {
        setHistoryModal(undefined);
    }

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(category)) {
                next.delete(category);
            } else {
                next.add(category);
            }
            return next;
        });
    };

    const histoyTypeTK = (type: StockHistoryType): TK => {
        switch (type) {
            case StockHistoryType.Appointment:
                return TK.appointment;
            case StockHistoryType.Expense:
                return TK.purchase;
            case StockHistoryType.Extra:
                return TK.extras;
            case StockHistoryType.Manual:
                return TK.manual;
            default:
                return TK.unknown;
        }
    }

    return (
        <div className='stockPageContainer'>
            <div className="stockPageHeader">
                <h2>{t(TK.stock)}</h2>
                <AppButton
                    id="create-stock-button"
                    color="success"
                    onClick={handleOpenCreateModal}
                    style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)'}}
                >
                    <Add />
                    {t(TK.create)}
                </AppButton>
            </div>

            {isLoading ? (
                <Typography><i>{t(TK.loading)}...</i></Typography>
            ) : !stock?.length ? (
                <Typography><i>{t(TK.noStockItemsYet)}</i></Typography>
            ) : (
                <div className="stockTreeContainer">
                    {Array.from(stockByCategory.entries()).map(([category, items]) => {
                        const isExpanded = expandedCategories.has(category);
                        const totalCount = items.reduce((sum, item) => sum + item.amount, 0);
                        const minAmount = Math.min(...items.map(item => item.amount));
                        const categoryColor = minAmount <= redStockLimit ? 'red' : minAmount <= yellowStockLimit ? 'orange' : 'inherit';

                        return (
                            <div key={category} className="categoryNode">
                                <div
                                    className="categoryHeader"
                                    onClick={() => toggleCategory(category)}
                                >
                                    {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                                    {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
                                    <span className="categoryName">{category}</span>
                                    <span
                                        className="categoryCount"
                                        style={{
                                            color: categoryColor,
                                            fontWeight: minAmount < 5 ? 'bold' : 'normal'
                                        }}
                                    >
                                        ({items.length} {t(TK.items)}, {totalCount} {t(TK.total)})
                                    </span>
                                </div>
                                {isExpanded && (
                                    <div className="categoryItems">
                                        {items.map(item => (
                                            <div
                                                key={item.id}
                                                id={`stock-item-${item.id}`}
                                                className="stockItem"
                                            >
                                                <div>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleOpenEditModal(item)}
                                                        title={t(TK.edit)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <span className="itemName">{item.name}</span>
                                                </div>
                                                <div className="itemActions">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleOpenHistory(item)}
                                                        disabled={loadingHistory}
                                                        title={t(TK.history)}
                                                    >
                                                        <ListIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleOpenDecreaseModal(item)}
                                                        title={t(TK.updateStockQuantity)}
                                                    >
                                                        <RemoveIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleOpenBuyModal(item)}
                                                        title={t(TK.buyStock)}
                                                    >
                                                        <ShoppingCartIcon fontSize="small" />
                                                    </IconButton>
                                                    <span
                                                        className="itemCount"
                                                        style={{
                                                            backgroundColor: item.amount <= redStockLimit ? 'red' : item.amount <= yellowStockLimit ? 'orange' : '#e0e0e0',
                                                            color: item.amount <= yellowStockLimit ? 'white' : 'unset',
                                                            fontWeight: item.amount <= yellowStockLimit ? 'bold' : 'normal'
                                                        }}
                                                    >
                                                        {item.amount}
                                                    </span>
                                                </div>
                                                
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <Modal isOpen={!!buyStock} toggle={handleCloseBuyModal} size='lg'>
                <ModalHeader toggle={handleCloseBuyModal}>
                    {t(TK.buyStock)} - {buyStock?.item.name}
                </ModalHeader>
                <ModalBody>
                    {buyStock &&
                        <ExpenseForm
                            data={buyStock?.expense}
                            onChange={expense => setBuyStock({...buyStock, expense})}
                            canEdit
                            hasChanged
                            noRecurring
                            onSave={handleCreateExpense}
                            type='create'
                        />
                    }
                </ModalBody>
            </Modal>

            <Modal isOpen={!!decreaseStock} toggle={handleCloseDecreaseModal}>
                <ModalHeader toggle={handleCloseDecreaseModal}>
                    {t(TK.updateStockQuantity)} - {decreaseStock?.item.name}
                </ModalHeader>
                <ModalBody>
                    {decreaseStock && (
                        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
                            <NumberInputField
                                id={`stock-quantity-${decreaseStock.item.id}`}
                                label={t(TK.quantity)}
                                value={decreaseStock.data.quantity}
                                onChange={quantity => setDecreaseStock({
                                    ...decreaseStock,
                                    data: {...decreaseStock.data, quantity: quantity || 0}
                                })}
                            />
                            <TextInputField
                                id={`stock-reason-${decreaseStock.item.id}`}
                                label={t(TK.reason)}
                                type='textarea'
                                value={decreaseStock.data.reason}
                                onChange={reason => setDecreaseStock({
                                    ...decreaseStock,
                                    data: {...decreaseStock.data, reason}
                                })}
                                placeholder={t(TK.optional)}
                            />
                            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '10px'}}>
                                <span><b>{t(TK.total)}:</b> {decreaseStock.item.amount + decreaseStock.data.quantity} {t(TK.items)}</span>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={handleCloseDecreaseModal}>{t(TK.cancel)}</AppButton>
                    <AppButton
                        color="success"
                        onClick={handleDecreaseStock}
                        disabled={!decreaseStock || submitting}
                    >
                        {t(TK.update)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!historyModal} toggle={handleCloseHistory} size='lg'>
                <ModalHeader toggle={handleCloseHistory}>
                    {t(TK.history)} - {historyModal?.item.name}
                </ModalHeader>
                <ModalBody>
                    {loadingHistory ? (
                        <Typography><i>{t(TK.loading)}...</i></Typography>
                    ) : historyModal?.history.length === 0 ? (
                        <Typography><i>{t(TK.empty)}</i></Typography>
                    ) : (
                        <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                            <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                                    <tr style={{borderBottom: '2px solid #ddd'}}>
                                        <th style={{padding: '10px', textAlign: 'left'}}>{t(TK.date)}</th>
                                        <th style={{padding: '10px', textAlign: 'left'}}>{t(TK.type)}</th>
                                        <th style={{padding: '10px', textAlign: 'right'}}>{t(TK.quantity)}</th>
                                        <th style={{padding: '10px', textAlign: 'left'}}>{t(TK.user)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyModal?.history.map((historyItem, index) => (
                                        <tr key={historyItem.id || index} style={{borderBottom: '1px solid #eee'}}>
                                            <td style={{padding: '10px'}}>{moment(historyItem.date).format('YYYY-MM-DD')}</td>
                                            <td style={{padding: '10px'}}>{t(histoyTypeTK(historyItem.type))}{historyItem.notes ? ` - ${historyItem.notes}` : ''}</td>
                                            <td style={{padding: '10px', textAlign: 'right'}}>{historyItem.quantity}</td>
                                            <td style={{padding: '10px'}}>{historyItem.username || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={handleCloseHistory}>{t(TK.close)}</AppButton>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default StockPage;
